/*--------------------------------------------------------------
 * Custom data action: set-user-data-and-addresses
 * Calls custom Commerce endpoint Commerce/SetUserDataAndAddresses to add/edit a user with associated addresses.
 *--------------------------------------------------------------*/
import * as Msdyn365 from '@msdyn365-commerce/core';

export interface IUserAddressEntry {
    RecId: number;
    Name?: string;
    Street?: string;
    StreetNumber?: string;
    ZipCode?: string;
    City?: string;
    Country?: string;
    State?: string;
    LocationId: string;
    [key: string]: unknown; // Allow backend to accept additional address properties
}

export interface IUserDataAndAddressesContainer {
    BusinessPartnerId?: string;
    [key: string]: unknown;
    Addresses?: IUserAddressEntry[];
}

export interface ISetUserDataAndAddressesPayload {
    userDataAndAddresses: IUserDataAndAddressesContainer;
}

export interface ISetUserDataAndAddressesResponse {
    success: boolean;
    status: number;
    error?: string;
}

export class SetUserDataAndAddressesInput implements Msdyn365.IActionInput {
    public payload: ISetUserDataAndAddressesPayload;

    constructor(payload: ISetUserDataAndAddressesPayload) {
        this.payload = payload;
    }

    public getCacheKey = (): string => `SetUserDataAndAddresses-${this.payload.userDataAndAddresses.BusinessPartnerId || 'new'}`;
    public getCacheObjectType = (): string => 'SetUserDataAndAddresses';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

const createInput = (): Msdyn365.IActionInput => {
    return new SetUserDataAndAddressesInput({ userDataAndAddresses: {} });
};

async function action(input: SetUserDataAndAddressesInput, ctx: Msdyn365.IActionContext): Promise<ISetUserDataAndAddressesResponse> {
    const userToken = ctx.requestContext.user.token;

    try {
        // Ensure Addresses array contains full address objects (preserve all properties)
        const normalizedPayload: ISetUserDataAndAddressesPayload = JSON.parse(JSON.stringify(input.payload || {}));
        const userDataContainer = normalizedPayload.userDataAndAddresses as Record<string, unknown> | undefined;
        if (userDataContainer && Array.isArray(userDataContainer.Addresses)) {
            userDataContainer.Addresses = userDataContainer.Addresses.map(originalAddr => {
                const src = (originalAddr || {}) as Record<string, any>;

                // Prefer RecId if provided, otherwise fall back to RecordId.
                const recId = Number(src.RecId ?? src.RecordId) || 0;

                const normalizedAddress: Record<string, unknown> = {
                    RecId: recId,
                    Name: src.Name,
                    Street: src.Street,
                    StreetNumber: src.StreetNumber,
                    ZipCode: src.ZipCode,
                    City: src.City,
                    Country: src.Country,
                    State: src.State,
                    LocationId: src.LocationId
                };

                // Preserve LocationId if available under several possible property names
                const locationIdCandidate = src.LocationId ?? src.LocationID ?? src.Location ?? src.LocationIdString ?? src.LocationIdText;
                if (locationIdCandidate !== undefined && locationIdCandidate !== null) {
                    normalizedAddress.LocationId = String(locationIdCandidate);
                }

                return normalizedAddress;
            });
        }

        const headers: Record<string, string> = {
            oun: ctx.requestContext.apiSettings.oun,
            'Content-Type': 'application/json'
        };
        if (userToken) {
            headers.Authorization = `id_token ${userToken}`;
        }

        const response = await Msdyn365.sendRequest<ISetUserDataAndAddressesResponse>(
            `${ctx.requestContext.apiSettings.baseUrl}Commerce/SetUserDataAndAddresses`,
            'post',
            normalizedPayload,
            { headers }
        );
        if (response.status === 200) {
            return { success: true, status: response.status };
        }
        return { success: false, status: response.status, error: response.data?.error || 'Request failed' };
    } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        ctx.telemetry.trace(`SetUserDataAndAddresses exception: ${message}`);
        return { success: false, status: 0, error: message };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ISetUserDataAndAddressesResponse>>action,
    id: 'SetUserDataAndAddresses',
    input: createInput
});
